import type { TemplateComponentComponentType } from '@unifyapps/defs/blocks/TemplateComponent/types';
import type { BlockId, BlockType } from '@unifyapps/defs/types/block';
import type { DeviceVariantType } from '@unifyapps/defs/types/deviceVariant';
import type { DevicesOverrides, InterfacePageEntity } from '@unifyapps/defs/types/page';
import type { Delta } from '@unifyapps/defs/types/page/deviceOverrides';
import InterfacePageHelper from '../../../helper/InterfacePage';
import { getActiveInterfacePageFromStore, getActivePage } from '../runner/state';
import type { InterfaceStoreType } from './types';

export {
  getActiveInterfacePageFromStore,
  getActivePage,
  getBlocksFromPage,
  getInterfaceRecord,
  getPageVariableDetails,
} from '../runner/state';

export function selectIsComponentOverridesMode(state: InterfaceStoreType): boolean {
  const history = state.propertyPanel.history;
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i]?.viewDetails.viewType === 'COMPONENT_OVERRIDE_DETAILS') return true;
  }
  return false;
}

export function selectParentTemplateComponentBlockId(
  state: InterfaceStoreType,
): BlockId | undefined {
  const history = state.propertyPanel.history;
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i]?.viewDetails.viewType === 'COMPONENT_OVERRIDE_DETAILS') {
      return (
        history[i]?.viewDetails.data as { parentTemplateComponentBlockId?: BlockId } | undefined
      )?.parentTemplateComponentBlockId;
    }
  }
  return undefined;
}

/**
 * Imperative mirror of useActiveBlockForPanel. In component overrides mode, reads the
 * active child block directly from componentOverrides on the parent block in
 * the consumer page — never reads from inner template pages.
 */
export function getActiveBlockFromPanel(
  state: InterfaceStoreType,
  device: DeviceVariantType,
): BlockType | undefined {
  const { activeBlockId } = state;
  if (!activeBlockId) return undefined;

  const isOverridesMode = selectIsComponentOverridesMode(state);
  const parentId = selectParentTemplateComponentBlockId(state);
  const activePage = getActiveInterfacePage(state);
  if (!activePage) return undefined;

  if (!isOverridesMode || !parentId) {
    return InterfacePageHelper.getBlockFromInterfacePage(activePage, device, activeBlockId) as
      | BlockType
      | undefined;
  }

  // component-overrides path: the override entry IS the block — stored on the parent in the consumer page
  const parentBlock = InterfacePageHelper.getBlockFromInterfacePage(activePage, device, parentId) as
    | BlockType<TemplateComponentComponentType>
    | undefined;

  return parentBlock?.component.content.componentOverrides?.[activeBlockId];
}

export const getActiveBlockFromPage = (
  store: { getState: () => InterfaceStoreType },
  device: DeviceVariantType,
) => {
  const state = store.getState();

  const activeBlockId = state.activeBlockId;
  const activeInterfacePage = getActiveInterfacePageFromStore(state);

  if (!activeInterfacePage) {
    return undefined;
  }

  const deviceBlock = InterfacePageHelper.getDeviceBlock(
    activeInterfacePage,
    device,
    activeBlockId,
  );
  const baseDeviceBlock = InterfacePageHelper.getBaseDeviceBlock(
    activeInterfacePage,
    activeBlockId,
  );

  return deviceBlock ?? baseDeviceBlock;
};

export const getActiveInterfacePage = (
  store: Pick<InterfaceStoreType, 'activeInterfacePage'>,
): InterfacePageEntity | undefined => {
  return store.activeInterfacePage;
};

export const getPartialDevicesOverrides = (store: InterfaceStoreType, blockIds: BlockId[]) => {
  const activeInterfacePage = store.activeInterfacePage;

  if (!activeInterfacePage) {
    return {};
  }

  const devices = InterfacePageHelper.getDevices(activeInterfacePage);

  const partialDevices: Partial<DevicesOverrides> = {};

  if (!devices) {
    return {};
  }

  for (const device of Object.keys(devices)) {
    const deviceOverride = devices[device as DeviceVariantType];
    if (deviceOverride) {
      partialDevices[device as DeviceVariantType] = {
        blocks: blockIds.reduce(
          (acc, id: BlockId) => {
            if (id) {
              const block = deviceOverride.blocks?.[id];
              if (block) {
                acc[id] = block;
              }
            }

            return acc;
          },
          {} as Record<string, BlockType>,
        ),
        diffs: {
          blocks: blockIds.reduce(
            (acc, id: BlockId) => {
              if (id) {
                const diff = deviceOverride.diffs?.blocks?.[id];
                if (diff) {
                  acc[id] = diff;
                }
              }

              return acc;
            },
            {} as Record<string, Delta>,
          ),
        },
      };
    }
  }

  return partialDevices;
};

export const getActiveStepId = (getState: () => InterfaceStoreType) => {
  const state = getState();
  return state.walkthrough.activeStepId;
};

export const getActiveStep = (getState: () => InterfaceStoreType) => {
  const activePage = getActivePage(getState);
  const activeStepId = getActiveStepId(getState);
  return activePage?.properties.walkthrough?.steps?.find((step) => step.id === activeStepId);
};
